import { alpha, lighten } from '@mui/material/styles';
import { common } from '@mui/material/colors';
import theme, { brand, neutral } from '../../theme';

/**
 * Colour tokens for the studio's dark player (the picture, the transport bar
 * and the labels laid over the video) and for the light timeline, derived
 * from the theme's `brand` and `neutral` tokens so components carry no hex
 * values of their own. Shared by the annotation studio, the editor workspace
 * and every other screen that uses the studio player.
 *
 * Text contrast on `surface` (ink): text 17.8:1, textMuted 11.6:1,
 * textSubtle 7.8:1, accent 5.6:1 (copper itself is only 3.6:1 on ink, so the
 * dark player uses the lighter copper, and only for marks and short labels
 * on the solid bar). Laid over a white frame of video, `overlay` still keeps
 * textMuted at 9.2:1. `studioTokens.test.ts` checks these pairs.
 */
export const studioDark = {
  /** Letterbox behind the picture. */
  stage: common.black,
  /** The transport bar and other solid dark surfaces. */
  surface: brand.ink,
  /** Labels laid over the picture (notes at the playhead, In/Out marks). */
  overlay: alpha(brand.ink, 0.9),
  /** Dims the picture behind an error message. */
  scrim: alpha(common.black, 0.75),
  /** A raised element on the dark surface, e.g. the scrub bar's time label. */
  raised: brand.slate,
  /** Primary text (the timecode readout). */
  text: common.white,
  /** Control icons and secondary text. */
  textMuted: alpha(common.white, 0.8),
  /** Tertiary text (total duration, "Buffering…"). */
  textSubtle: alpha(common.white, 0.64),
  hover: alpha(common.white, 0.08),
  rail: alpha(common.white, 0.24),
  /** Disabled controls (exempt from contrast rules). */
  disabled: alpha(common.white, 0.32),
  disabledFill: alpha(common.white, 0.24),
  /** Active state on dark: played progress, loop on, shuttle speed. */
  accent: brand.copperLight,
  accentHalo: alpha(brand.copperLight, 0.3),
  /** The In → Out range on the scrub bar. */
  rangeFill: alpha(brand.copperLight, 0.45),
  focusRing: common.white,
  /** The round play button. */
  buttonFill: common.white,
  buttonFillHover: neutral[200],
  buttonText: brand.ink,
} as const;

/** Tokens for the light timeline (ruler, lanes, markers, hover card). */
export const studioTimeline = {
  panel: common.white,
  /** Ruler and lane-label gutter. */
  gutter: brand.canvas,
  tickMajor: alpha(brand.slate, 0.55),
  tickMinor: alpha(brand.slate, 0.25),
  /** NLE-style red playhead (the theme's bright red), apart from copper In/Out marks. */
  playhead: theme.palette.error.light,
  /** Outline of a selected or focused item. */
  selected: brand.ink,
  /** Ring that separates a point marker from what is under it. */
  markerRing: common.white,
  /** Labels on solid task bars. */
  onSolid: common.white,
  barShadow: `0 1px 2px ${alpha(brand.ink, 0.18)}`,
  /** Resize handles on a hovered bar. */
  handle: alpha(common.white, 0.45),
  popover: brand.ink,
  popoverText: common.white,
  popoverShadow: `0 8px 24px ${alpha(brand.ink, 0.24)}`,
} as const;

/** The transport redraws its marks while playing; the handful of category colours are worked out once. */
const liftedColors = new Map<string, string>();

/**
 * A category colour lifted for marks drawn on the dark transport, where the
 * dark category hues fall under 3:1 against ink. Same hue, so a blue note is
 * still blue; anything that is not a colour MUI can parse is returned as is.
 */
export const markOnDark = (color: string): string => {
  const cached = liftedColors.get(color);
  if (cached) return cached;
  let lifted = color;
  try {
    lifted = lighten(color, 0.3);
  } catch {
    // Not a colour MUI can parse: draw it as given.
  }
  liftedColors.set(color, lifted);
  return lifted;
};
